
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './AuthorizedUser.css';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { getAppData, getProgramData, formatFirstAndLastName, validateFirstAndLastName, isDateValid, areStringsEqual, getNameAndDOBFromSessionStorage, formatDateOfBirth, getAcquisitionFlow, AcquisitionFlow } from '../../utils/acquisition-utils';
import successIcon from '../../designs.cob-corp-acq/icon-success.svg';
import useActiveElement from '../../hooks/useActiveElement';
import SessionStorage from '../../utils/SessionStorage';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { InputCheckbox, InputRadio } from 'component-library/index';
import { Section, Row, Col, Button } from '../../importCL';
import NameField from '../FieldContainers/NameField/NameField';
import { MiddleInitialField } from '../FieldContainers/MiddleInitialField/MiddleInitialField';
import DateOfBirthField from '../FieldContainers/DateOfBirthField/DateOfBirthField';
import NameFieldWithProvider from '../FieldContainers/NameField/NameFieldWithProvider';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import MiddleInitialFieldWithProvider from '../FieldContainers/MiddleInitialField/MiddleInitialFieldWithProvider';
import AcquisitionErrorToggleAuthorizedUserEvent from '../../utils/custom-events/AcquisitionErrorToggleAuthorizedUserEvent';
import AcquisitionErrorToggleEvent from '../ApplicationCta/AcquisitionErrorToggleEvent';
import { useAllFieldSubscription } from '../../providers/ModernAcquisitionProvider/useSubscription';
import { DateOfBirthFieldWithProvider } from '../FieldContainers/DateOfBirthField/DateOfBirthFieldWithProvider';
import { generateFieldErrorMessage } from '../../utils/error-message';
interface IAuthorizedUserProps extends MappedComponentProperties {
    title?: string
    topBorder?: boolean
    bottomBorder?: boolean
    instructionalText?: string
    description?: string
    spouseText?: string
    dobErrorText?: string
    disclosureHeadline?: string
    disclosureShortDesc?: string
    disclosureLongDesc?: string
    feeHeadline?: string
    feeDescription?: string
    zeroFeeDescription?: string
    benefitIcon?: { iconAltText: string, iconPath: string }
    benefits?: [{ title: string[], subTitle: string[] }]
    feeNoticeText?: string;
}

interface IFieldsFocusedOnce {
    firstName: boolean;
    middleInitial: boolean;
    lastName: boolean;
    dateOfBirth: boolean;
}

interface IVisitedRef {
    day: boolean;
    month: boolean;
    year: boolean;
    isInside: boolean;
}

export const AuthorizedUserEditConfig = {
    emptyLabel: 'Authorized User',
    isEmpty: (props: PropsWithChildren<IAuthorizedUserProps>) => {
        return !props || !props.title;
    },
};

const AuthorizedUser = (props: PropsWithChildren<IAuthorizedUserProps>): JSX.Element => {
    const activeElement = useActiveElement();
    const firstNameInput = useRef<HTMLInputElement>(null);
    const middleNameInput = useRef<HTMLInputElement>(null);
    const lastNameInput = useRef<HTMLInputElement>(null);
    const dobInput = useRef<HTMLInputElement & { clearFields: (b?: any) => void }>(null);
    const fieldsFocusedOnce = useRef<IFieldsFocusedOnce | null>(null);

    const [showRest, setShowRest] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [isSpouse, setIsSpouse] = useState<boolean | undefined>(undefined);
    const [readMore, setReadMore] = useState(false);
    const [authorizedUserFee, setAuthorizedUserFee] = useState(19);
    const [showSection, setShowSection] = useState<boolean>(true);

    const visitedRef = useRef<IVisitedRef>({
        day: false,
        month: false,
        year: false,
        isInside: true,
    });
    const [showGreenIcon, setShowGreenIcon] = useState<boolean>(false);
    const [clickedInOtherDateField, setClickedInOtherDateField] = useState<boolean>(false);
    const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
    const [isTabKey, setIsTabKey] = useState<boolean>(false);
    const [whichNameIsBlurred, setWhichNameIsBlurred] = useState<string>('');
    const isExperienceFragment = props.cqPath.toLowerCase().includes('experiencefragment');

    const handleFocus = () => {
        if (authorizedUserValidity.authorizedUserSpouse === false) {
            setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserSpouse: true }));
        }
    };

    const {
        setAuthorizedUserInfo,
        setIsAuthorisedUserSectionValid,
        isApplicationInfoRecieved,
        isAuthorizedUserInfoSaved,
        personalInfoForm,
        isAuthorisedUserSectionValid,
        isAuthorizedUserInEditMode,
        isSamePersonError,
        setIsSamePersonError,
        isPersonalInfoInEditMode,
        authorizedUserValidity,
        setAuthorizedUserValidity,
        authorizedUserInfo,
    } = useAcquisitionContext();
    const { getFeatureFlags } = useFeatureFlags();
    const { getPersonalInformation, personalInformationWrite, setIsAuthorizedUserSelected, getAuthorizedUserDob, getPersonalInformationDob } = useAcquisitionProviderFacade();


    const { fields } = useAllFieldSubscription();
    const { authorizedUserFirstName, authorizedUserLastName, authorizedUserDateOfBirthDay, authorizedUserDateOfBirthMonth, authorizedUserDateOfBirthYear } = fields;
    const checkFormValidity = async (): Promise<boolean> => {
        await handleIsSamePerson();
        if (isSamePersonError) {
            return false;
        }
        if (!showRest) {
            return true;
        }

        if (getFeatureFlags('DPM-14218')) {
            const authorizedUserDob = getAuthorizedUserDob();
            if (!authorizedUserDob) {
                return false
            }
        } else {
            if (!dateOfBirth || dateOfBirth === '//') {
                return false;
            }
        }

        if (getFeatureFlags('DPM-13102')) {
            const acquisitionData = getPersonalInformation();
            if (
                !acquisitionData?.authorizedUserFirstName?.value ||
                !acquisitionData?.authorizedUserLastName?.value ||
                isSpouse === undefined
            ) {
                return false;
            }

            if (
                !validateFirstAndLastName(acquisitionData?.authorizedUserFirstName?.value) ||
                !validateFirstAndLastName(acquisitionData?.authorizedUserLastName?.value)
            ) {
                return false;
            }
        } else {
            if (!firstName || !lastName || isSpouse === undefined) {
                return false;
            }

            if (!validateFirstAndLastName(firstName) || !validateFirstAndLastName(lastName)) {
                return false;
            }
        }


        if (getFeatureFlags('DPM-14218')) {
            const authorizedUserDob = getAuthorizedUserDob();
            return isDateValid(authorizedUserDob, 'au')
        } else {
            if (dateOfBirth) {
                return isDateValid(dateOfBirth, 'au');
            }
        }

        return true;
    };

    const handleSuccessFocus = (field: string) => {
        // only used for legacy fields. will not be used if DPM-13102 flag is active
        switch (field) {
            case 'firstName':
                setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserFirstName: true }));
                break;
            case 'lastName':
                setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserLastName: true }));
                break;
            default:
                break;
        }
        const updatedFocusedFields = {
            ...fieldsFocusedOnce.current,
            [field]: true,
        };
        // todo: update lazy typing
        fieldsFocusedOnce.current = updatedFocusedFields as any;
    };
    const clearFieldsCheckBox = () => {
        visitedRef.current = {
            day: false,
            month: false,
            year: false,
            isInside: false,
        };
        fieldsFocusedOnce.current = {
            firstName: false,
            middleInitial: false,
            lastName: false,
            dateOfBirth: false,
        };
        personalInformationWrite({
            authorizedUserDateOfBirthDay: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserDateOfBirthMonth: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserDateOfBirthYear: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserFirstName: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserLastName: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserMiddleInitial: { value: '', state: 'neutral', errorType: 'None' },
            authorizedUserSpouse: { value: null, state: 'neutral', errorType: 'None' },
        });
        // Only clear local fields if flag is off
        if (!getFeatureFlags('DPM-13102')) {
            setFirstName('');
            setLastName('');
            setMiddleName('');
        }

        setIsSpouse(undefined);
        setDateOfBirth('//');
        setClickedInOtherDateField(false);
        setIsTabKey(false);
        dobInput.current?.clearFields();
        setIsSamePersonError(false);
    };

    useEffect(() => {
        if (!fieldsFocusedOnce.current) {
            fieldsFocusedOnce.current = {
                firstName: false,
                middleInitial: false,
                lastName: false,
                dateOfBirth: false,
            } as IFieldsFocusedOnce;
        }
    }, []);
    useEffect(() => {
        handleIsSamePerson();
    }, [isPersonalInfoInEditMode]);

    useEffect(() => {
        getAppData().then(fullAppData => {
            let isSelected = false;
            if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                isSelected = fullAppData?.AuthorizedUser?.IsAuthorizedUserSelected ?? false;
            } else {
                isSelected = fullAppData && fullAppData.AuthorizedUser && fullAppData.AuthorizedUser.IsAuthorizedUserSelected;
            }

            if (isAuthorizedUserInfoSaved && isSelected && isExperienceFragment) {
                fieldsFocusedOnce.current = {
                    firstName: false,
                    middleInitial: false,
                    lastName: false,
                    dateOfBirth: false,
                };
                setShowGreenIcon(false);
                visitedRef.current = {
                    day: true,
                    month: true,
                    year: true,
                    isInside: false,
                };
            } else {
                const isSubmit = activeElement?.id.toLowerCase().includes('continue');
                clearAllDateFieldsOrIgnore(isSubmit || false);
            }
        });
    }, [isAuthorizedUserInfoSaved, isAuthorizedUserInEditMode, isSamePersonError, isAuthorisedUserSectionValid]);

    useEffect(() => {
        getProgramData().then(async (programData) => {

            if (getAcquisitionFlow() === AcquisitionFlow.PHA) {
                const authFee = programData?.card?.keyCardBenefits?.availableAncillaries?.find((anc) => anc?.title === 'AUTHORIZED_USER')?.fee ?? 0;
                setAuthorizedUserFee(authFee);
            }

            if (programData.AuthorizedUserFeeInfo) {
                setAuthorizedUserFee(programData.AuthorizedUserFeeInfo.Fee);
            }

            await handleIsSamePerson();
            const showAuth = SessionStorage.get('pqOfOneAuthorizedUserShowing');
            const phaShowAuth = !!programData?.card?.keyCardBenefits?.availableAncillaries?.find((anc) => anc?.title === 'AUTHORIZED_USER');

            if (!props.isInEditor && !programData.AuthorizedUserEligible && !programData.IsAuthorizedUserEligible && showAuth !== 'true' && !phaShowAuth) {
                setShowSection(false);
            } else {
                //Reset fields when clicking edit from save
                if (!showSection) {
                    fieldsFocusedOnce.current = null;
                }
                setShowSection(true);
            }
        });

        getAppData().then(fullAppData => {
            if (fullAppData && fullAppData.AuthorizedUser && fullAppData.AuthorizedUser.IsAuthorizedUserSelected) {
                const authorizedUserData = fullAppData.AuthorizedUser;
                setShowRest(authorizedUserData.IsAuthorizedUserSelected);
                if (getFeatureFlags('DPM-13102')) {
                    personalInformationWrite({
                        authorizedUserFirstName: { value: authorizedUserData.AuthorizedUserFirstName },
                        authorizedUserLastName: { value: authorizedUserData.AuthorizedUserLastName },
                        authorizedUserMiddleInitial: { value: authorizedUserData.AuthorizedUserMiddleInitial },
                    });
                } else {
                    setFirstName(authorizedUserData.AuthorizedUserFirstName);
                    setLastName(authorizedUserData.AuthorizedUserLastName);
                    setMiddleName(authorizedUserData.AuthorizedUserMiddleInitial);
                }
                if (getFeatureFlags('DPM-14218')) {
                    personalInformationWrite({
                        authorizedUserDateOfBirthDay: { value: authorizedUserData.AuthorizedUserDOBDay },
                        authorizedUserDateOfBirthMonth: { value: authorizedUserData.AuthorizedUserDOBMonth },
                        authorizedUserDateOfBirthYear: { value: authorizedUserData.AuthorizedUserDOBYear },
                    });
                } 
                setDateOfBirth(authorizedUserData.AuthorizedUserDOBMonth + '/' + authorizedUserData.AuthorizedUserDOBDay + '/' + authorizedUserData.AuthorizedUserDOBYear);
                setIsSpouse(authorizedUserData.IsAuthorizedUserSpouse);

            }
        });
    }, [isApplicationInfoRecieved]);


    useEffect(() => {
        checkFormValidity().then(isValid => {
            setIsAuthorisedUserSectionValid(isValid);
        });
        if (!showRest) {
            // reset fields if auth user is not elected
            personalInformationWrite({
                authorizedUserDateOfBirthDay: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserDateOfBirthMonth: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserDateOfBirthYear: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserFirstName: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserLastName: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserMiddleInitial: { value: '', state: 'neutral', errorType: 'None' },
                authorizedUserSpouse: { value: null, state: 'neutral', errorType: 'None' },
            });
            setIsAuthorizedUserSelected(false);
        } else {
            const acquisitionData = getPersonalInformation();
            let dateOfBirthArr = [];
            if (getFeatureFlags('DPM-14218')) {
                dateOfBirthArr = [acquisitionData.authorizedUserDateOfBirthMonth.value, acquisitionData.authorizedUserDateOfBirthDay.value, acquisitionData.authorizedUserDateOfBirthYear.value]
            } else {
                dateOfBirthArr = dateOfBirth.split(/[\/-]/);
            }
            personalInformationWrite({
                authorizedUserDateOfBirthDay: { value: dateOfBirthArr[1] },
                authorizedUserDateOfBirthMonth: { value: dateOfBirthArr[0] },
                authorizedUserDateOfBirthYear: { value: dateOfBirthArr[2] },
                authorizedUserFirstName: {
                    value:
                        getFeatureFlags('DPM-13102') ?
                            acquisitionData?.authorizedUserFirstName?.value :
                            firstName
                },
                authorizedUserLastName: {
                    value:
                        getFeatureFlags('DPM-13102') ?
                            acquisitionData?.authorizedUserLastName?.value :
                            lastName
                },
                authorizedUserMiddleInitial: {
                    value:
                        getFeatureFlags('DPM-13102') ?
                            acquisitionData?.authorizedUserMiddleInitial?.value :
                            middleName
                },
                authorizedUserSpouse: { value: isSpouse },
            });
            setIsAuthorizedUserSelected(true);
        }

        const errorListener = (event: AcquisitionErrorToggleAuthorizedUserEvent | AcquisitionErrorToggleEvent | Event) => {
            if (showRest) {
                const acquisitionData = getPersonalInformation();
                const isFirstValid = getFeatureFlags('DPM-13102')
                    ? validateFirstAndLastName(acquisitionData?.authorizedUserFirstName?.value)
                    && !isSamePersonError
                    : validateFirstAndLastName(firstName);
                const isLastValid = getFeatureFlags('DPM-13102')
                    ? validateFirstAndLastName(acquisitionData?.authorizedUserLastName?.value)
                    && !isSamePersonError
                    : validateFirstAndLastName(lastName)

                const dob = getAuthorizedUserDob();
                const isDobValid = getFeatureFlags('DPM-14218') ? isDateValid(dob, 'au') && !isSamePersonError : isDateValid(dob, 'au');

                const isSpouseValid = isSpouse === true || isSpouse === false;
                const isVerificationEdit = (event as AcquisitionErrorToggleAuthorizedUserEvent)?.isDispatchFromVerification;

                personalInformationWrite({
                    authorizedUserFirstName: {
                        state: isFirstValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isFirstValid ? 'None' : 'requiredField',
                    },
                    authorizedUserLastName: {
                        state: isLastValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isLastValid ? 'None' : 'requiredField',
                    },
                    authorizedUserSpouse: {
                        state: isSpouseValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isSpouseValid ? 'None' : 'requiredField',
                    },
                    authorizedUserDateOfBirthMonth: {
                        state: isDobValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isDobValid ? 'None' : 'requiredField',
                    },
                    authorizedUserDateOfBirthDay: {
                        state: isDobValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isDobValid ? 'None' : 'requiredField',
                    },
                    authorizedUserDateOfBirthYear: {
                        state: isDobValid ? isVerificationEdit ? 'neutral' : 'success' : 'error',
                        errorType: isDobValid ? 'None' : 'requiredField',
                    }
                });
                clearAllDateFieldsOrIgnore(true);
            }
        };

        window.addEventListener('acquisitionErrorToggle', errorListener);
        window.addEventListener('acquisitionErrorToggle-authorizedUser', errorListener);
        return () => {
            window.removeEventListener('acquisitionErrorToggle', errorListener);
            window.removeEventListener('acquisitionErrorToggle-authorizedUser', errorListener);
        };
    }, [
        showRest,
        firstName, lastName, middleName,
        dateOfBirth,
        isSpouse, isSamePersonError,
        isAuthorisedUserSectionValid,
        authorizedUserFirstName.value, authorizedUserLastName.value, authorizedUserDateOfBirthDay.value, authorizedUserDateOfBirthMonth.value, authorizedUserDateOfBirthYear.value]);

    useEffect(() => {
        // skip if flag is off or auth user is not selected
        if (!getFeatureFlags('DPM-13102') || !showRest) {
            return;
        }

        // Ensure correct errors for modern fields if isSamePersonFlag updates
        if (isSamePersonError) {
            personalInformationWrite({
                authorizedUserFirstName: { state: 'error', errorType: 'invalidInput' },
                authorizedUserLastName: { state: 'error', errorType: 'invalidInput' },
            });
            if (getFeatureFlags('DPM-14218')) {
                personalInformationWrite({
                    authorizedUserDateOfBirthDay: { state: 'error', errorType: 'invalidInput' },
                    authorizedUserDateOfBirthMonth: { state: 'error', errorType: 'invalidInput' },
                    authorizedUserDateOfBirthYear: { state: 'error', errorType: 'invalidInput' },
                });
            }
        } else {
            const acquisitionData = getPersonalInformation();
            const isFirstValid = validateFirstAndLastName(acquisitionData?.authorizedUserFirstName?.value);
            const isLastValid = validateFirstAndLastName(acquisitionData?.authorizedUserLastName?.value);
            personalInformationWrite({
                authorizedUserFirstName: {
                    state: isFirstValid ? 'success' : 'error',
                    errorType: isFirstValid ? 'None' : 'invalidInput'
                },
                authorizedUserLastName: {
                    state: isLastValid ? 'success' : 'error',
                    errorType: isLastValid ? 'None' : 'invalidInput'
                },

            });
            if (getFeatureFlags('DPM-14218')) {
                personalInformationWrite({
                    authorizedUserDateOfBirthMonth: {
                        state: 'success',
                        errorType: 'None',
                    },
                    authorizedUserDateOfBirthDay: {
                        state: 'success',
                        errorType: 'None',
                    }, authorizedUserDateOfBirthYear: {
                        state: 'success',
                        errorType: 'None',
                    }
                });
            }
        }
    }, [isSamePersonError]);

    const handleOnBlur = async () => {
        let dob;

        if (getFeatureFlags('DPM-14218')) {
            dob = getAuthorizedUserDob();
        } else {
            dob = dateOfBirth;
        }
        const isValid = isDateValid(dob, 'au');
        if (dob.length === 10 && !isValid) {
            setAuthorizedUserValidity((prev) => ({
                ...prev,
                authorizedUserDob: false,
            }));
        } else if (dob.length === 10 && isValid){
            setAuthorizedUserValidity((prev) => ({
                ...prev,
                authorizedUserDob: true,
            }));
        }
        if (isMouseDown) {
            setClickedInOtherDateField(true);
        } else {
            setClickedInOtherDateField(false);
        }
        setShowGreenIcon(isValid);
        await handleIsSamePerson();
    };

    const handleIsSamePerson = async () => {

        const acquisitionData = getPersonalInformation();
        let normalizedDob;
        if (getFeatureFlags('DPM-14218')) {
            normalizedDob = getAuthorizedUserDob();
        } else {
            normalizedDob = dateOfBirth;
        }

        if (
            getFeatureFlags('DPM-13102') &&
            acquisitionData?.authorizedUserFirstName.value &&
            acquisitionData?.authorizedUserLastName?.value &&
            normalizedDob
        ) {
            const authorizedUserString = [acquisitionData?.authorizedUserFirstName.value, acquisitionData?.authorizedUserLastName.value, formatDateOfBirth(normalizedDob)].join('');

            let counterAppString;
            if (getAcquisitionFlow() === AcquisitionFlow.SkipPQ && SessionStorage.verifyNotEmpty('personalInfo')) {
                const personalInfo = await SessionStorage.getCipher('personalInfo');
                const dob = personalInfo?.DOBMonth + '/' + personalInfo?.DOBDay + '/' + personalInfo?.DOBYear;
                counterAppString = [personalInfo?.FirstName, personalInfo?.LastName, formatDateOfBirth(dob)].join('');
            }

            const [appDataFirstName, appDataLastName, appDataDOB] = await getNameAndDOBFromSessionStorage(getAcquisitionFlow() === AcquisitionFlow.SkipPQ && SessionStorage.verifyNotEmpty('prequalFullAppData') && !SessionStorage.verifyNotEmpty('personalInfo'));
            const verificationPageApplicantString = [appDataFirstName, appDataLastName, formatDateOfBirth(appDataDOB)].join('');
            const piDob = getPersonalInformationDob();

            const primaryApplicantString = [acquisitionData?.firstName?.value, acquisitionData?.lastName?.value, formatDateOfBirth(piDob)].join('');
            const isSamePerson = areStringsEqual(authorizedUserString, verificationPageApplicantString) || areStringsEqual(authorizedUserString, primaryApplicantString) || areStringsEqual(authorizedUserString, counterAppString)
            if (getFeatureFlags('DPM-14218')) {
                if (isSamePerson) {
                    personalInformationWrite({
                        authorizedUserDateOfBirthDay: { state: 'error', errorType: 'invalidInput' },
                        authorizedUserDateOfBirthMonth: { state: 'error', errorType: 'invalidInput' },
                        authorizedUserDateOfBirthYear: { state: 'error', errorType: 'invalidInput' },
                    });
                } else {
                    personalInformationWrite({
                        authorizedUserDateOfBirthMonth: {
                            state: 'success',
                            errorType: 'None',
                        },
                        authorizedUserDateOfBirthDay: {
                            state: 'success',
                            errorType: 'None',
                        }, authorizedUserDateOfBirthYear: {
                            state: 'success',
                            errorType: 'None',
                        }
                    });
                }
            }
            setIsSamePersonError(isSamePerson);
        } else if (lastName && firstName && normalizedDob) {
            // Legacy
            const authorizedUserString = [firstName, lastName, formatDateOfBirth(normalizedDob)].join('');
            let counterAppString;
            if (getAcquisitionFlow() === AcquisitionFlow.SkipPQ && SessionStorage.verifyNotEmpty('personalInfo')) {
                const personalInfo = await SessionStorage.getCipher('personalInfo', true);
                const dob = personalInfo?.DOBMonth + '/' + personalInfo?.DOBDay + '/' + personalInfo?.DOBYear;
                counterAppString = [personalInfo?.FirstName, personalInfo?.LastName, formatDateOfBirth(dob)].join('');
            }
            const [appDataFirstName, appDataLastName, appDataDOB] = await getNameAndDOBFromSessionStorage(getAcquisitionFlow() === AcquisitionFlow.SkipPQ && SessionStorage.verifyNotEmpty('prequalFullAppData') && !SessionStorage.verifyNotEmpty('personalInfo'));
            const verificationPageApplicantString = [appDataFirstName, appDataLastName, formatDateOfBirth(appDataDOB)].join('');
            const appPageApplicantString = [personalInfoForm.firstName.value, personalInfoForm.lastName.value, formatDateOfBirth(personalInfoForm.dob.value)].join('');
            const isSamePerson = areStringsEqual(authorizedUserString, verificationPageApplicantString) || areStringsEqual(authorizedUserString, appPageApplicantString) || areStringsEqual(authorizedUserString, counterAppString)
            if (getFeatureFlags('DPM-14218')) {
                if (isSamePerson) {
                    personalInformationWrite({
                        authorizedUserDateOfBirthDay: { state: 'error', errorType: 'invalidInput' },
                        authorizedUserDateOfBirthMonth: { state: 'error', errorType: 'invalidInput' },
                        authorizedUserDateOfBirthYear: { state: 'error', errorType: 'invalidInput' },
                    });
                } else {
                    personalInformationWrite({
                        authorizedUserDateOfBirthMonth: {
                            state: 'success',
                            errorType: 'None',
                        },
                        authorizedUserDateOfBirthDay: {
                            state: 'success',
                            errorType: 'None',
                        }, authorizedUserDateOfBirthYear: {
                            state: 'success',
                            errorType: 'None',
                        }
                    });
                }
            }
            setIsSamePersonError(isSamePerson);
        }
    };

    const clearAllDateFieldsOrIgnore = (isSubmit: boolean) => {
        const validDob = isDateValid(dateOfBirth, 'au');
        const activeElementId = activeElement?.id || '';
        const allInputsVisited = visitedRef.current.day && visitedRef.current.month && visitedRef.current.year || isSubmit;
        if (isSubmit) {
            visitedRef.current.isInside = false;
        } else {
            visitedRef.current.isInside = activeElementId?.includes('au__') || isTabKey || clickedInOtherDateField;
        }
        if (dobInput && allInputsVisited && !validDob && !visitedRef.current.isInside) {

            dobInput.current?.clearFields(true);

            setAuthorizedUserValidity((prev) => ({
                ...prev,
                authorizedUserDob: false,
            }));
        }
    };

    useEffect(() => {
        if (!whichNameIsBlurred) {
            return;
        }

        if (getFeatureFlags('DPM-13102') && isSamePersonError) {
            personalInformationWrite({
                authorizedUserFirstName: { ...authorizedUserFirstName, state: 'error', errorType: 'invalidInput' },
                authorizedUserLastName: { ...authorizedUserLastName, state: 'error', errorType: 'invalidInput' },
            })
            return;
        } else {
            if (whichNameIsBlurred === 'first') {
                setFirstName(formatFirstAndLastName(whichNameIsBlurred, firstName, true));
            } else {
                setLastName(formatFirstAndLastName(whichNameIsBlurred, lastName, true));
            }
        }
        setWhichNameIsBlurred('');
    }, [whichNameIsBlurred]);

    return showSection ? (<div id='authorized-user' className='AuthorizedUser'>
        <Section containerClass='AuthorizedUser__container'>
            {props.topBorder && <hr />}
            <h3 className='AuthorizedUser__title'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</h3>
            {props.benefits && (
                <div className='AuthorizedUser__benefits'>
                    {props.benefits.map((benefit, index) => {
                        return (
                            <div key={index} className='AuthorizedUser__benefit'>
                                {props.benefitIcon && (
                                    <img className='AuthorizedUser__benefitIcon'
                                        loading='lazy'
                                        src={props.benefitIcon?.iconPath}
                                        alt={props.benefitIcon?.iconAltText} />
                                )}
                                <div className='AuthorizedUser__benefitText'>
                                    <div className='AuthorizedUser__benefitTitle'>{transformHTMLParserLinks(wrapTrademarkSuperscript(benefit.title[0]))}</div>
                                    <div className='AuthorizedUser__benefitSubtitle'>{transformHTMLParserLinks(wrapTrademarkSuperscript(benefit.subTitle[0]))}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {props.feeNoticeText && authorizedUserFee !== 0 &&
                <div className='AuthorizedUser__feeNoticeText'>
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(props.feeNoticeText))}
                </div>
            }
            <div className='AuthorizedUser__formContainer'>
                <InputCheckbox
                    id='auth-user-yes'
                    checkboxId='authorized-user-checkbox'
                    name='check'
                    label={transformHTMLParserLinks(wrapTrademarkSuperscript(props.description))}
                    setValue={(val: boolean) => {
                        clearFieldsCheckBox();
                        setShowRest(val);
                    }}
                    value={showRest}
                    labelClass='AuthorizedUser__agreeLabel'
                />
                {showRest && <div>
                    <Row>
                        <Col md={4} lg={4} id='auFirstName-field-container'>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='authorizedUserFirstName'
                                    name='First Name'
                                    label='First Name'
                                    id='auFirstName'
                                    onBlurCallback={async () => {
                                        setWhichNameIsBlurred('first');
                                        await handleIsSamePerson();
                                    }}
                                />
                            ) : (
                                <NameField
                                    ref={firstNameInput}
                                    label='First Name'
                                    value={firstName}
                                    name='First Name'
                                    id='auFirstName'
                                    error={isSamePersonError || (!authorizedUserValidity.authorizedUserFirstName && activeElement !== firstNameInput.current)}
                                    valid={[authorizedUserValidity.authorizedUserFirstName, (isValid) => setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserFirstName: isValid }))]}
                                    setValueCallback={(value: string) => {
                                        setFirstName(formatFirstAndLastName('first', value ?? ''));
                                    }}
                                    onBlurCallback={async () => {
                                        setWhichNameIsBlurred('first');
                                        await handleIsSamePerson();
                                    }}
                                    onFocusCallback={() => handleSuccessFocus('firstName')}
                                    alternate
                                    isAU
                                    isAUInEditMode={isAuthorizedUserInEditMode}
                                />
                            )}
                        </Col>
                        <Col md={4} lg={4}>
                            {getFeatureFlags('DPM-13102') ? (
                                <MiddleInitialFieldWithProvider
                                    fieldKey='authorizedUserMiddleInitial'
                                    name='Middle Initial'
                                    label='Middle Initial (optional)'
                                    id='auMiddleInitial'
                                />
                            ) : (
                                <MiddleInitialField
                                    alternate
                                    ref={middleNameInput}
                                    label='Middle Initial (optional)'
                                    value={middleName}
                                    className='AuthorizedUser__middleInitial'
                                    name='Middle Initial'
                                    id='auMiddleInitial'
                                    setValueCallback={(value: string) => {
                                        value !== '' ? setMiddleName(value[0].toUpperCase().replace(/[^a-zA-Z]/, '')) : setMiddleName(value);
                                    }}
                                    onFocusCallback={() => handleSuccessFocus('middleInitial')}
                                    isAUInEditMode={isAuthorizedUserInEditMode}
                                />
                            )}
                        </Col>
                        <Col md={4} lg={4} id='auLastName-field-container'>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='authorizedUserLastName'
                                    name='Last Name'
                                    label='Last Name'
                                    id='auLastName'
                                    onBlurCallback={async () => {
                                        setWhichNameIsBlurred('last');
                                        await handleIsSamePerson();
                                    }}
                                />
                            ) : (
                                <NameField
                                    ref={lastNameInput}
                                    label='Last Name'
                                    value={lastName}
                                    name='Last Name'
                                    id='auLastName'
                                    error={isSamePersonError || (!authorizedUserValidity.authorizedUserLastName && activeElement !== lastNameInput.current)}
                                    valid={[authorizedUserValidity.authorizedUserLastName, (isValid) => setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserLastName: isValid }))]}
                                    setValueCallback={(value: string) => {
                                        setLastName(formatFirstAndLastName('last', value ?? ''));
                                    }}
                                    onFocusCallback={() => handleSuccessFocus('lastName')}
                                    onBlurCallback={async () => {
                                        setWhichNameIsBlurred('last');
                                        await handleIsSamePerson();
                                    }}
                                    alternate
                                    isAU
                                    isAUInEditMode={isAuthorizedUserInEditMode}
                                />
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4} lg={4} id='auDateOfBirth-field-container' className='AuthorizedUser__inputContainer'>
                            {getFeatureFlags('DPM-14218') ?
                                <DateOfBirthFieldWithProvider
                                    label='Date of Birth'
                                    helperText='MM-DD-YYYY'
                                    id='au'
                                    isAuthorizedUser
                                    onBlurCallback={async () => { await handleOnBlur() }}
                                    errorMessage={isSamePersonError ? 'Full Name and Date of Birth cannot match yours' : props.dobErrorText || generateFieldErrorMessage('Date of Birth', !getAuthorizedUserDob() ? 'missingValue' : null)}
                                />
                                :
                                <>
                                    <DateOfBirthField
                                        isAuthorizedUser
                                        ref={dobInput}
                                        id='au'
                                        inputClass='AuthorizedUser__input'
                                        label='Date of Birth (MM-DD-YYYY)'
                                        value={dateOfBirth}
                                        name='Date'
                                        valid={[authorizedUserValidity.authorizedUserDob, (isValid) => setAuthorizedUserValidity((prev) => ({ ...prev, authorizedUserDob: isValid }))]}
                                        error={isSamePersonError}
                                        invalidErrorMessage={isSamePersonError ? 'Full Name and Date of Birth cannot match yours' : props.dobErrorText || generateFieldErrorMessage('Date of Birth')}
                                        errorText={isSamePersonError ? 'Full Name and Date of Birth cannot match yours' : props.dobErrorText || generateFieldErrorMessage('Date of Birth', !getAuthorizedUserDob() ? 'missingValue' : null)}
                                        onFocusCallback={() => setShowGreenIcon(false)}
                                        onBlurCallback={() => handleOnBlur()}
                                        onKeyDown={(e: any) => {
                                            if (e.shiftKey && activeElement?.id.includes('month') || !e.shiftKey && activeElement?.id.includes('year')) {
                                                setIsTabKey(false);
                                            } else if (e.key === 'Tab') {
                                                setIsTabKey(true);
                                            }
                                        }}
                                        onKeyUp={() => setIsTabKey(false)}
                                        onMouseDown={() => setIsMouseDown(true)}
                                        onMouseUp={() => setIsMouseDown(false)}
                                        required
                                        setValueCallback={setDateOfBirth}
                                        ageMin={15}
                                        ageMax={101}
                                        applicantStateOrAu='au'
                                        alternate
                                    />
                                    <div className='AuthorizedUser__successIcon'>
                                        {!isSamePersonError && dateOfBirth && dateOfBirth.length === 10 && isDateValid(dateOfBirth, 'au') && activeElement !== dobInput.current && authorizedUserValidity.authorizedUserDob && showGreenIcon && (
                                            <img loading='lazy' src={successIcon} />
                                        )}
                                    </div>
                                </>
                            }
                        </Col>
                    </Row>
                    <Row id='auSpouse-field-container' className='px-4'>
                        <p className={`AuthorizedUser__spouseText ${!authorizedUserValidity.authorizedUserSpouse ? 'AuthorizedUser__spouseText--error' : ''}`}>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.spouseText))}<span className='AuthorizedUser__requiredStar'>*</span></p>
                    </Row>
                    <Row className='px-4'>
                        <Col md={4} lg={4} className='AuthorizedUser__radioContainer'>
                            <span onFocus={handleFocus}>
                                <InputRadio
                                    label='Yes'
                                    value='true'
                                    name='yes_no_group'
                                    id='auSpouseYes'
                                    error={!authorizedUserValidity.authorizedUserSpouse}
                                    setValue={() => {
                                        setIsSpouse(true);
                                        setAuthorizedUserInfo((prev) => ({
                                            ...prev,
                                            IsAuthorizedUserSpouse: true,
                                        }));
                                    }
                                    }
                                    checked={authorizedUserInfo.IsAuthorizedUserSpouse !== undefined && authorizedUserInfo.IsAuthorizedUserSpouse === true}
                                />
                            </span>
                            <span onFocus={handleFocus}>
                                <InputRadio
                                    label='No'
                                    value='false'
                                    name='yes_no_group'
                                    id='auSpouseNo'
                                    error={!authorizedUserValidity.authorizedUserSpouse}
                                    setValue={() => {
                                        setIsSpouse(false);
                                        setAuthorizedUserInfo((prev) => ({
                                            ...prev,
                                            IsAuthorizedUserSpouse: false,
                                        }));
                                    }
                                    }
                                    checked={authorizedUserInfo.IsAuthorizedUserSpouse !== undefined && authorizedUserInfo.IsAuthorizedUserSpouse === false}
                                />
                            </span>
                        </Col>
                    </Row>
                    <Row className='px-4'>
                        <h4 className='AuthorizedUser__headlinesText'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.feeHeadline))}</h4>
                    </Row>
                    <Row className='px-4'>
                        {props.feeDescription && (Number(authorizedUserFee) !== 0 || !props.zeroFeeDescription) && (
                            <div className='AuthorizedUser__descriptionsText'>
                                {transformHTMLParserLinks(props.feeDescription.replace('${fee}', '$' + authorizedUserFee))}
                            </div>
                        )}

                        {(Number(authorizedUserFee) === 0 && props.zeroFeeDescription) && (
                            <div className='AuthorizedUser__descriptionsText'>
                                {transformHTMLParserLinks(props.zeroFeeDescription)}
                            </div>
                        )}
                    </Row>
                    <Row className='px-4'>
                        <h4 className='AuthorizedUser__headlinesText'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclosureHeadline))}</h4>
                    </Row>
                    <Row className='px-4'>
                        {!readMore && <div className='AuthorizedUser__descriptionsText'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclosureShortDesc))}<Button onClick={() => {
                            setReadMore(true);
                        }} className='AuthorizedUser__readMore' variant='link'>Read More.</Button></div>}
                        {readMore && props.disclosureLongDesc && <div><p className='AuthorizedUser__descriptionsText'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclosureShortDesc))}</p><p className='AuthorizedUser__descriptionsText'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclosureLongDesc))}</p></div>}
                    </Row>
                </div>}
            </div>
            {props.bottomBorder && <hr className='AuthorizedUser__bottomHr' />}
        </Section>
    </div>) : <React.Fragment></React.Fragment>;
};

export default AuthorizedUser;
